import { Copy, Check } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { TOAST_DURATION_MS } from '@/constants/timing'
import { cn } from '@/lib/utils'
import {
  CONTACT_TITLE_LABELS,
  type ContactTitle,
} from '@/lib/schemas/contact.schema'
import { usePortalContacts } from '@/hooks/use-portal'
import { PortalAddContactModal } from './portal-add-contact-modal'
import { PortalContactActionsMenu } from './portal-contact-actions-menu'
import type { PortalContact } from '@/services/portal.service'
import { formatContactName } from '@/lib/contacts/format-contact-name'

export function PortalContactsSection() {
  const { data, isLoading } = usePortalContacts()
  const [isAddContactOpen, setIsAddContactOpen] = useState(false)

  if (isLoading) {
    return (
      <div className="space-y-3 border-b border-primary/10 pb-3">
        <Typography variant="h6">Company Contacts</Typography>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </div>
    )
  }

  const contacts = data?.data ?? []
  const isOddVisibleCount = contacts.length % 2 === 1

  if (contacts.length === 0) {
    return (
      <div className="space-y-3 border-b border-primary/10 pb-3">
        <Typography variant="h6">Company Contacts</Typography>
        <div className="rounded-md border border-primary/20 bg-white/50 p-3 dark:bg-gray-900">
          <Typography className="text-sm text-primary">
            No primary contact assigned
          </Typography>
          <Typography className="text-xs text-primary/70">
            Add a primary contact to keep communication centralized.
          </Typography>
          <Button
            type="button"
            variant="badge"
            size="badge"
            className="mt-2"
            onClick={() => setIsAddContactOpen(true)}
          >
            Add primary contact +
          </Button>
        </div>

        <PortalAddContactModal
          open={isAddContactOpen}
          onOpenChange={setIsAddContactOpen}
          forcePrimaryContact
        />
      </div>
    )
  }

  return (
    <div className="space-y-4 border-b border-primary/10 pb-3">
      <Typography variant="h6">Company Contacts</Typography>
      <div className="grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2">
        {contacts.map((contact, index) => {
          const isLastCard = index === contacts.length - 1
          const shouldSpanFullWidth = isLastCard && isOddVisibleCount

          return (
            <div
              key={contact.id}
              className={shouldSpanFullWidth ? 'sm:col-span-2' : ''}
            >
              <PortalContactCard contact={contact} />
            </div>
          )
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="badge"
          size="badge"
          onClick={() => setIsAddContactOpen(true)}
        >
          Add new contact +
        </Button>
      </div>

      <PortalAddContactModal
        open={isAddContactOpen}
        onOpenChange={setIsAddContactOpen}
      />
    </div>
  )
}

function PortalContactCard({ contact }: { contact: PortalContact }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!contact.email) return
    await navigator.clipboard.writeText(contact.email)
    setCopied(true)
    setTimeout(() => setCopied(false), TOAST_DURATION_MS)
  }

  const titleLabel = contact.title
    ? (CONTACT_TITLE_LABELS[contact.title as ContactTitle] ?? contact.title)
    : ''

  return (
    <div
      className={cn(
        'self-start rounded-md border border-primary/20 bg-white/50 p-2 dark:bg-gray-900'
      )}
    >
      <div className="flex items-start justify-between">
        <Typography
          as="span"
          variant="body-sm"
          className="block font-medium text-primary/80"
        >
          {titleLabel}
        </Typography>
        <PortalContactActionsMenu contact={contact} className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <Typography
          as="span"
          variant="body"
          className="block font-medium text-primary"
        >
          {formatContactName(contact)}
        </Typography>
        {contact.email ? (
          <div className="flex justify-between">
            <Typography
              as="span"
              variant="body-sm"
              className="mt-0.5 block truncate text-primary/70"
            >
              {contact.email}
            </Typography>
            <Button
              size="icon"
              variant="ghost"
              className="h-5 w-5 shrink-0"
              onClick={handleCopy}
              aria-label={copied ? 'Email copied' : 'Copy email'}
            >
              {copied ? (
                <Check className="h-3 w-3 text-primary/20" />
              ) : (
                <Copy className="h-3 w-3 text-primary/20" />
              )}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
